/// <reference types="vite/client" />

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  GithubAuthProvider,
  OAuthProvider,
  type Auth,
} from "firebase/auth";
import type { Analytics } from "firebase/analytics";

const firebaseConfig: Record<string, any> = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let provider: GoogleAuthProvider | null = null;
let githubProvider: GithubAuthProvider | null = null;
let db: any = null; // lazily initialized
let analytics: Analytics | null = null;
let performance: any = null;

try {
  // Basic validation to avoid runtime crashes when env vars are missing
  if (!firebaseConfig.apiKey) {
    throw new Error("Missing Firebase API key (VITE_FIREBASE_API_KEY).");
  }

  app = initializeApp(firebaseConfig as any);
  auth = getAuth(app);
  provider = new GoogleAuthProvider();
  githubProvider = new GithubAuthProvider();

  // Defer analytics and performance initialization to after initial paint
  // This reduces JavaScript execution time during critical rendering path
  if (import.meta.env.PROD && typeof window !== "undefined") {
    // Use requestIdleCallback for better performance, setTimeout as fallback
    const scheduleInit = () => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(() => initAnalyticsAndPerformance());
      } else {
        setTimeout(() => initAnalyticsAndPerformance(), 1);
      }
    };

    const initAnalyticsAndPerformance = async () => {
      try {
        // Dynamically import analytics and performance to avoid module-level imports
        const [{ getAnalytics }, { getPerformance }] = await Promise.all([
          import("firebase/analytics"),
          import("firebase/performance"),
        ]);
        analytics = getAnalytics(app!);
        performance = getPerformance(app!);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(
          "Firebase analytics/performance init failed:",
          (e as Error).message || e,
        );
      }
    };

    scheduleInit();
  }
} catch (err) {
  // Avoid throwing — log helpful message and export nulls so the app can handle it
  // The runtime error previously caused a black screen; this prevents that.
  // Ensure you set the VITE_FIREBASE_* env vars at build time (see README or CI).
  // eslint-disable-next-line no-console
  console.error(
    "Firebase initialization skipped:",
    err && (err as Error).message ? (err as Error).message : err,
  );
}

// Initialize App Check (ReCAPTCHA v3) if site key provided.
// Uses a debug token when `VITE_FIREBASE_APPCHECK_DEBUG_TOKEN` is set (local/dev testing).
(async function initAppCheck() {
  if (typeof window === "undefined" || !app) return;

  const siteKey = import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY;
  const debugToken = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN;

  if (!siteKey && !debugToken) return;

  try {
    if (debugToken) {
      // Set debug token before initializing App Check (required for debug mode)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
    }

    const mod = await import("firebase/app-check");
    const { initializeAppCheck, ReCaptchaV3Provider } = mod;

    if (siteKey) {
      initializeAppCheck(app as any, {
        provider: new ReCaptchaV3Provider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
      // eslint-disable-next-line no-console
      console.log("Firebase App Check initialized.");
    } else {
      // If only debugToken provided (no site key), initialize with debug provider
      initializeAppCheck(app as any, {
        provider: new (mod.ReCaptchaV3Provider as any)("unused"),
        isTokenAutoRefreshEnabled: true,
      });
      // eslint-disable-next-line no-console
      console.log("Firebase App Check initialized in debug mode.");
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("App Check init failed:", e);
  }
})();

// Lazily initialize and return the Realtime Database instance to avoid bundling
// firebase/database into the main vendor chunk when it's not needed by all pages.
export async function getDatabaseInstance(): Promise<any | null> {
  if (db) return db;
  if (!app) {
    // eslint-disable-next-line no-console
    console.warn("Firebase app not initialized; cannot get database instance.");
    return null;
  }

  try {
    const mod = await import("firebase/database");
    db = mod.getDatabase(app);
    return db;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to dynamically load firebase/database:", e);
    return null;
  }
}

export { app, auth, provider, githubProvider, analytics, performance };

// NOTE: To use GitHub sign-in, enable the provider in the Firebase Console
// (Authentication → Sign-in method) and register a GitHub OAuth App. Add
// the Client ID / Client Secret in Firebase, and ensure your project's
// authorized domains and OAuth redirect URIs are configured.
export default app;
